/**
 * EnergyMapService — Brain Calibration Session 2: The Emotional Narrative
 *
 * Maps the emotional trajectory of a track across time.
 * Goal: Can indii identify where the "Soul" peaks in a 4-minute journey?
 *
 * Takes audio segments (from AudioAnalysisService) + the full audio file,
 * then uses Gemini to produce a time-stamped emotional arc with tension peaks,
 * release moments, and narrative phase labels.
 */
import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import { INTELLIGENCE_MODELS } from '@/core/config/intelligence-models';
import type { Schema } from '@/shared/types/ai.dto';
import { Logger } from '@/core/logger/Logger';
import { withServiceError } from '@/lib/errors';
import type { AudioFeatures } from './types';

export interface EmotionalBeat {
    /** Timestamp in seconds where this emotional beat occurs */
    timestamp: number;
    /** Normalized energy at this moment (0.0–1.0) */
    energy: number;
    /** Human-readable phase label (e.g., "Intro", "Build", "Drop", "Breakdown", "Outro") */
    phase: string;
    /** Core emotion at this beat (e.g., "Tension", "Release", "Euphoria", "Grief", "Resolve") */
    emotion: string;
    /** One-sentence description of what's happening sonically */
    sonicNote: string;
}

export interface EmotionalNarrative {
    /** Ordered emotional beats across the track */
    arc: EmotionalBeat[];
    /** Index of the arc entry where the soul peaks (highest combined emotional + sonic intensity) */
    soulPeakIndex: number;
    /** Overall trajectory shape */
    trajectoryShape: 'Gradual Build' | 'Explosive Open' | 'Rollercoaster' | 'Slow Burn' | 'Plateau' | 'Declining';
    /** What makes this track emotionally distinct */
    emotionalSignature: string;
    /** Tension-to-release ratio (0 = all release, 1 = all tension) */
    tensionRatio: number;
}

const ENERGY_MAP_SCHEMA: Schema = {
    type: 'OBJECT' as const,
    properties: {
        arc: {
            type: 'ARRAY',
            items: {
                type: 'OBJECT',
                properties: {
                    timestamp: { type: 'NUMBER' },
                    energy: { type: 'NUMBER' },
                    phase: { type: 'STRING' },
                    emotion: { type: 'STRING' },
                    sonicNote: { type: 'STRING' }
                },
                required: ['timestamp', 'energy', 'phase', 'emotion', 'sonicNote']
            }
        },
        soulPeakIndex: { type: 'NUMBER' },
        trajectoryShape: { type: 'STRING' },
        emotionalSignature: { type: 'STRING' },
        tensionRatio: { type: 'NUMBER' }
    },
    required: ['arc', 'soulPeakIndex', 'trajectoryShape', 'emotionalSignature', 'tensionRatio']
} as unknown as Schema;

export class EnergyMapService {
    /**
     * Generates a full emotional arc map for an audio file.
     * Combines local segment data with Gemini's deep listening.
     */
    async mapEmotionalArc(
        file: File,
        technicalFeatures: AudioFeatures
    ): Promise<EmotionalNarrative> {
        return withServiceError('EnergyMap', 'mapEmotionalArc', async () => {
            void file;
            void technicalFeatures;
            throw new Error(
                'Raw browser master audio cannot be sent to Gemini. Use the protected canonical-master analysis receipt or a bounded desktop proxy.'
            );
        });
    }

    /**
     * Generates an emotional arc using an already-compressed proxy base64 string.
     */
    async mapEmotionalArcWithProxy(
        base64Audio: string,
        mimeType: string,
        technicalFeatures: AudioFeatures
    ): Promise<EmotionalNarrative> {
        return withServiceError('EnergyMap', 'mapEmotionalArcWithProxy', async () => {
            Logger.info('EnergyMap', `Mapping emotional arc (using proxy data)`);

            const duration = technicalFeatures.duration;
            const segmentContext = this.formatSegments(technicalFeatures.segments);

            const prompt = `
You are a master music therapist and narrative composer.
LISTEN to this track from start to finish — ${Math.round(duration)}s total — and map its emotional journey.

Technical Context:
- BPM: ${Math.round(technicalFeatures.bpm)}
- Key: ${technicalFeatures.key} ${technicalFeatures.scale}
- Overall Energy: ${(technicalFeatures.energy * 100).toFixed(0)}%
- Danceability: ${(technicalFeatures.danceability * 100).toFixed(0)}%
${segmentContext ? `- Detected Segments (local analysis): ${segmentContext}` : ''}

=== YOUR TASK ===

Produce an 'arc' of 6–12 EmotionalBeats that capture the track's journey.
Each beat needs a timestamp (in seconds from 0 to ${Math.round(duration)}),
a normalized energy (0.0–1.0), a phase label, an emotion, and a sonic note.

Then identify:
- 'soulPeakIndex': Which arc index holds the highest combined emotional intensity?
  This is where the track HITS. Trust the audio, not the math.
- 'trajectoryShape': The macro shape of the emotional journey.
- 'emotionalSignature': What makes THIS track's emotional arc unique in 1–2 sentences?
- 'tensionRatio': Float from 0.0 (pure release/joy) to 1.0 (pure tension/darkness).

RULES:
- Do not cluster all beats in the first 30 seconds. Spread them across the full duration.
- Timestamps must be strictly ascending.
- 'soulPeakIndex' must be a valid index into the 'arc' array.
- Listen for dynamics — a quiet moment AFTER a peak is emotionally significant.
- If the track doesn't peak emotionally, say so via trajectoryShape = 'Plateau'.
`;

            const narrative = await AutonomousIntelligence.generateStructuredData<EmotionalNarrative>(
                [
                    { text: prompt },
                    {
                        inlineData: {
                            mimeType: mimeType,
                            data: base64Audio
                        }
                    }
                ],
                ENERGY_MAP_SCHEMA,
                4096,
                'You are an expert in music emotion analysis and narrative arc mapping.',
                INTELLIGENCE_MODELS.TEXT.AGENT
            );

            Logger.info('EnergyMap', `Arc mapped: ${narrative.arc.length} beats, soul peak at index ${narrative.soulPeakIndex}, shape: ${narrative.trajectoryShape}`);
            return narrative;
        });
    }

    private formatSegments(segments?: AudioFeatures['segments']): string {
        if (!segments?.length) return '';
        return segments
            .map((s: { start: number; label: string; energy: number }) => `${s.start.toFixed(0)}s: ${s.label} (energy ${(s.energy * 100).toFixed(0)}%)`)
            .join(', ');
    }

}

export const energyMapService = new EnergyMapService();
